// IndexedDB-backed draft persistence for the in-browser video recorder.
// Drafts survive page reloads for up to 12 hours; the DraftAutoPublisher
// component picks them up after that window and publishes them with safe defaults.

export const DRAFT_DB_NAME = 'globaleyes-drafts';
export const DRAFT_STORE = 'recordings';
export const DRAFT_KEY = 'current';
export const DRAFT_EXPIRY_MS = 12 * 60 * 60 * 1000; // 12 hours
export const MAX_RECORD_SECONDS = 180; // 3-minute hard cap

export const DEFAULT_LOCATION = { lat: 37.0902, lng: -95.7129, city: "", country: "" };

// Pick the cheapest duration bracket that can contain a recording of N seconds.
export const selectDurationBracket = (seconds) => {
  if (seconds <= 8) return 8;
  if (seconds <= 16) return 16;
  if (seconds <= 32) return 32;
  if (seconds <= 64) return 64;
  if (seconds <= 120) return 120;
  return 180;
};

const openDraftDB = () => new Promise((resolve, reject) => {
  if (!('indexedDB' in window)) return reject(new Error('no-idb'));
  const req = indexedDB.open(DRAFT_DB_NAME, 1);
  req.onupgradeneeded = (e) => {
    const db = e.target.result;
    if (!db.objectStoreNames.contains(DRAFT_STORE)) {
      db.createObjectStore(DRAFT_STORE);
    }
  };
  req.onsuccess = (e) => resolve(e.target.result);
  req.onerror = () => reject(req.error);
});

export const saveDraft = async ({ blob, startedAt, recordedSeconds, location }) => {
  try {
    const db = await openDraftDB();
    const tx = db.transaction(DRAFT_STORE, 'readwrite');
    tx.objectStore(DRAFT_STORE).put({ blob, startedAt, recordedSeconds, location }, DRAFT_KEY);
    await new Promise((res, rej) => {
      tx.oncomplete = res;
      tx.onerror = () => rej(tx.error);
    });
  } catch (err) {
    console.warn('saveDraft failed', err);
  }
};

export const loadDraft = async () => {
  try {
    const db = await openDraftDB();
    return await new Promise((resolve) => {
      const tx = db.transaction(DRAFT_STORE, 'readonly');
      const req = tx.objectStore(DRAFT_STORE).get(DRAFT_KEY);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  } catch (err) {
    return null;
  }
};

export const clearDraft = async () => {
  try {
    const db = await openDraftDB();
    const tx = db.transaction(DRAFT_STORE, 'readwrite');
    tx.objectStore(DRAFT_STORE).delete(DRAFT_KEY);
    await new Promise((res) => { tx.oncomplete = res; });
  } catch (err) { /* ignore */ }
};
